/* import __COLOCATED_TEMPLATE__ from './postings-list-item.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { stripTags } from 'sh-www/utils/misc';
import { htmlSafe } from '@ember/template';
import { showModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

import { APPLICATION_SIMPLE_STATUS } from 'sh-www/models/application';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { ASSIGNMENT_INVITE } from 'sh-www/models/assignment-invite';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';

const CLASS_NAME = 'SH-c-postings-list-item';
const SHORTENED_TEXT_LENGTH = 200;
const DEADLINE_WARNING_DAYS_BEFORE = 3;
const COLLAPSED_POSTING_ALLOWED_TAGS = ['a'];

class AdminPostingsListItem extends Component {
  @service constants;
  @service moment;
  @service router;
  @service store;

  @tracked descriptionExpanded = false;

  #descriptionStrippedTagsCache = createCache(() => {
    return stripTags(this.args.posting.description, COLLAPSED_POSTING_ALLOWED_TAGS);
  });

  #descriptionTextCache = createCache(() => {
    if (!this.descriptionExpanded && this.isDescriptionExpandable) {
      const substr = this.descriptionStrippedTags.substring(0, SHORTENED_TEXT_LENGTH);
      return `${substr}...`;
    }

    return htmlSafe(this.args.posting.description);
  });

  get className() {
    return CLASS_NAME;
  }

  get title() {
    return htmlSafe(this.args.posting.title);
  }

  get descriptionStrippedTags() {
    return getValue(this.#descriptionStrippedTagsCache);
  }

  get isDescriptionExpandable() {
    return this.descriptionStrippedTags.length > SHORTENED_TEXT_LENGTH;
  }

  get descriptionText() {
    return getValue(this.#descriptionTextCache);
  }

  get isPrivate() {
    return this.args.posting.privacyLevel === ASSIGNMENT.PRIVACY_LEVEL.PRIVATE;
  }

  get privacyLevelText() {
    switch(this.args.posting.privacyLevel) {
      case ASSIGNMENT.PRIVACY_LEVEL.PUBLIC:
        return 'Public';
      case ASSIGNMENT.PRIVACY_LEVEL.NETWORK:
        return 'Network';
      case ASSIGNMENT.PRIVACY_LEVEL.PRIVATE:
        return 'Private';
      case ASSIGNMENT.PRIVACY_LEVEL.GROUP:
        return 'Group';
      default:
        return '';
    }
  }

  get typeText() {
    switch(this.args.posting.type) {
      case ASSIGNMENT.TYPES.STANDARD:
        return 'Standard';
      case ASSIGNMENT.TYPES.PITCH:
        return 'Pitch';
      default:
        return '';
    }
  }

  get blastCount() {
    const { blasts } = this.args.posting;
    return blasts.reduce((accum, blast) => {
      return accum += blast.blastCount;
    }, 0);
  }

  get lastBlasted() {
    const { blasts } = this.args.posting;
    if (!blasts.length) return '';

    const dates = blasts.map((blast) => {
      return blast.createdAt;
    });

    // sorted oldest to newest
    return this.moment.moment(dates[dates.length - 1]).format('MMM Do, h:mm A');
  }

  get rateTypeText() {
    return this.args.posting.rateType === ASSIGNMENT.RATE_TYPE.FLAT ? 'Flat Rate' : 'Day Rate';
  }

  get startDateTimestamp() {
    const { posting } = this.args;
    const momentService = this.moment;

    switch (this.args.posting.status) {
      case ASSIGNMENT.STATUS.LIVE:
      case ASSIGNMENT.STATUS.CLOSED:
        return momentService.moment(posting.publishedAt).format('MM/DD/YYYY');
      case ASSIGNMENT.STATUS.DRAFT:
        return momentService.moment(posting.createdAt).format('MM/DD/YYYY');
      case ASSIGNMENT.STATUS.IN_REVIEW:
        return momentService.moment(posting.updatedAt).format('MM/DD/YYYY');
    }
  }

  get deadlineShouldWarn() {
    const deadlineDate = this.moment.moment(this.args.posting.deadline);
    const compareToDate = this.moment.moment().add(DEADLINE_WARNING_DAYS_BEFORE, 'days');
    return compareToDate.isAfter(deadlineDate, 'day');
  }

  get deadlineDateTimestamp() {
    const { posting } = this.args;
    return this.moment.moment(posting.deadline).format('MM/DD/YYYY');
  }

  @task
  *loadProjectsBySimpleStatus(assignmentId, simpleStatus) {
    try {
      const params = {
        adminSearch: true,
        filter: {
          assignmentId,
          simpleStatus,
        },
        include: 'seller',
      };

      return yield this.store.query('application', params);
    } catch(e) {
      handleErrorResponseWithModal(this, e);
    }
  }

  @action
  toggleProperty(prop) {
    this[prop] = !this[prop];
  }

  @action
  onBlasterClicked(postingId) {
    this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__BLASTER, postingId);
  }

  @action
  onPendingProjectsClicked() {
    const { posting } = this.args;
    showModal(this, 'admin/project-prices', {
      loadProjectsTaskInstance: this.loadProjectsBySimpleStatus.perform(posting.id, APPLICATION_SIMPLE_STATUS.PENDING),
      title: 'Pending Projects',
    });
  }

  @action
  onActiveProjectsClicked() {
    const { posting } = this.args;
    showModal(this, 'admin/project-prices', {
      loadProjectsTaskInstance: this.loadProjectsBySimpleStatus.perform(posting.id, APPLICATION_SIMPLE_STATUS.ACTIVE),
      title: 'Active Projects',
    });
  }

  @action
  onCompletedProjectsClicked() {
    const { posting } = this.args;
    showModal(this, 'admin/project-prices', {
      loadProjectsTaskInstance: this.loadProjectsBySimpleStatus.perform(posting.id, APPLICATION_SIMPLE_STATUS.COMPLETED),
      title: 'Completed Projects',
    });
  }

  @action
  onDeclinedProjectsClicked() {
    const { posting } = this.args;
    showModal(this, 'admin/project-prices', {
      loadProjectsTaskInstance: this.loadProjectsBySimpleStatus.perform(posting.id, APPLICATION_SIMPLE_STATUS.DECLINED),
      title: 'Declined Projects',
    });
  }
}

export default AdminPostingsListItem;
